import type { StoryblokConfigSlug } from '~/types/storyblokConfigSlug';
import log from '~/utils/log';

type SectionRegistryEntry = Pick<StoryblokConfigSlug, 'country' | 'sectionId' | 'professionalPortalProductMount'> &
    Partial<Pick<StoryblokConfigSlug, 'configPath'>>;

export function findDuplicateSectionIds(registry: SectionRegistryEntry[]): string[] {
    const keys = new Set<string>();
    const duplicates = new Set<string>();

    for (const config of registry) {
        const key = `${config.country}:${config.sectionId}`;
        if (keys.has(key)) {
            duplicates.add(key);
            continue;
        }
        keys.add(key);
    }

    return [...duplicates];
}

export function warnOnDuplicateSectionIds(
    registry: SectionRegistryEntry[],
    logKey: string,
    context: Record<string, unknown> = {}
): string[] {
    const duplicateSectionIds = findDuplicateSectionIds(registry);
    if (!duplicateSectionIds.length) return duplicateSectionIds;

    log.warn(`[Section Registry] ${logKey}`, {
        duplicateSectionIds,
        affectedConfigs: registry
            .filter((config) => duplicateSectionIds.includes(`${config.country}:${config.sectionId}`))
            .map(({ configPath, country, sectionId }) => ({ configPath, country, sectionId })),
        ...context
    });

    return duplicateSectionIds;
}

export function findDuplicateProfessionalPortalProductMountCountries(registry: SectionRegistryEntry[]): string[] {
    const mountCountries = new Set<string>();
    const duplicates = new Set<string>();

    for (const config of registry) {
        if (config.professionalPortalProductMount === true) {
            if (mountCountries.has(config.country)) {
                duplicates.add(config.country);
            } else {
                mountCountries.add(config.country);
            }
        }
    }

    return [...duplicates];
}

export function warnOnDuplicateProfessionalPortalProductMounts(registry: SectionRegistryEntry[]): string[] {
    const duplicateCountries = findDuplicateProfessionalPortalProductMountCountries(registry);
    if (!duplicateCountries.length) return duplicateCountries;

    log.warn('[Section Registry] Duplicate professional portal product mounts found; keeping all registry entries', {
        duplicateCountries,
        affectedConfigs: registry
            .filter(
                (config) =>
                    config.professionalPortalProductMount === true && duplicateCountries.includes(config.country)
            )
            .map(({ configPath, country, sectionId }) => ({ configPath, country, sectionId }))
    });

    return duplicateCountries;
}
